<template>
  <Dialog :open="showCreateBoostRewardsModal" @update:open="(e) => onClose(e)">
    <DialogContent>
      <DialogHeader class="text-center">
        <template v-if="currentTab === 'create'">
          <DialogTitle>Create Boost Rewards</DialogTitle>
          <DialogDescription>Make sure the plan is correct.</DialogDescription>
        </template>
        <template v-if="currentTab === 'send'">
          <DialogTitle>Send Link</DialogTitle>
          <DialogDescription>Select a way to share the boost rewards.</DialogDescription>
        </template>
      </DialogHeader>

      <SectionDivider className="my-4" />
      <template v-if="currentTab === 'create'">
        <div class="flex flex-col gap-4">
          <h2 class="font-semibold text-grey-light-900">Voucher Details</h2>
          <div class="flex justify-between text-grey-light-700 capitalize">
            <span>Voucher Provider</span>
            <span>{{ voucherDetails.providerName.toLocaleLowerCase() }}</span>
          </div>
          <div class="flex justify-between text-grey-light-700">
            <span>Min Goal</span>
            <span>{{ displayCurrency(voucherDetails.minimumSavingGoalInCents) }}</span>
          </div>
          <div class="flex justify-between text-grey-light-700">
            <span>Max Goal</span>
            <span>{{ displayCurrency(voucherDetails.maximumSavingGoalInCents) }}</span>
          </div>
        </div>

        <SectionDivider className="my-4" />
        <div class="flex flex-col gap-4">
          <h2 class="font-semibold text-grey-light-900">Reward Details</h2>
          <div class="flex justify-between items-center text-grey-light-700">
            <span>Reward type</span>
            <span class="text-grey-light-900 font-semibold capitalize">
              {{ voucherDetails.rewardType?.toLocaleLowerCase() }}
            </span>
          </div>
          <div class="flex justify-between items-center text-grey-light-700">
            <span>Reward Amount</span>
            <span class="text-grey-light-900 font-semibold text-xl" v-if="isFlatAmount">
              {{ displayCurrency(voucherDetails.rewardFlatAmountInCents) }}
            </span>
            <span class="text-grey-light-900 font-semibold text-xl" v-else-if="!isFlatAmount">
              {{ voucherDetails.rewardPercentage }}%
            </span>
          </div>
        </div>
      </template>

      <SendOrderLinkTab
        v-if="currentTab === 'send'"
        :completed-order="completedOrder"
        :boost-rewards="isBoostRewards"
      />

      <DialogFooter>
        <template v-if="currentTab === 'create'">
          <Button @click="currentTab = 'send'">Create Boost Rewards</Button>
        </template>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import SectionDivider from '@/components/common/SectionDivider.vue';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useMerchantStore } from '@/stores/merchants';
import { displayCurrency } from '@/utils/format.utils';
import { ref } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';
import SendOrderLinkTab from '../create-order-modal/tabs/SendOrderLinkTab.vue';

defineProps<{
  showCreateBoostRewardsModal: boolean;
}>();
const emit = defineEmits<{
  (e: 'update:show-create-boost-rewards-modal', value: boolean): void;
}>();

const merchantStore = useMerchantStore();

const isBoostRewards = ref(true);
const currentTab = ref<'create' | 'send'>('create');
const completedOrder = ref<ModelOrderResponse>();
const isFlatAmount = merchantStore.merchant?.voucherSettings?.rewardType === 'FLAT' ? true : false;
const voucherDetails = ref({
  maximumSavingGoalInCents: merchantStore.merchant?.voucherSettings?.maximumSavingGoalInCents || 0,
  minimumSavingGoalInCents: merchantStore.merchant?.voucherSettings?.minimumSavingGoalInCents || 0,
  rewardFlatAmountInCents: merchantStore.merchant?.voucherSettings?.rewardFlatAmountInCents || 0,
  rewardPercentage: merchantStore.merchant?.voucherSettings?.rewardPercentage || 0,
  rewardType: merchantStore.merchant?.voucherSettings?.rewardType || '',
  voucherProviders: merchantStore.merchant?.voucherSettings?.voucherProviders || [],
  providerName: merchantStore.merchant?.voucherSettings?.voucherProviders?.[0].name || '',
});

const onClose = (open: boolean) => {
  if (!open) {
    setTimeout(() => (currentTab.value = 'create'), 350);
    completedOrder.value = undefined;

    isBoostRewards.value = true;
    emit('update:show-create-boost-rewards-modal', false);
  }
};
</script>
